import React, { useCallback, useEffect, useRef, useState } from "react";
import DiamondDivider from "../theme/DiamondDivider";
import {
  fetchCurrentUser,
  getUsageLimits,
  practiceCancelOpenLobby,
  practiceCreateOpenLobby,
  practiceJoinOpenLobby,
  practiceLobbyStatus,
  practiceOpenLobbies,
  practicePledgeAccept,
  practiceSessionAccept,
  practiceSessionCancel,
  practiceSessionComplete,
  practiceSessionDecline,
  practiceSessionJoin,
  practiceSessionReport,
  updateProfile,
  transcribeSpeech,
  autoSuggestReplies,
  correctTranscript,
  savePracticeCallChat,
  saveWordBankSentence,
} from "../api";
import CallChat, { stripHarakat, shortName } from "./CallChat";
import { hasSpeechContent, isWhisperHallucination } from "../utils/speechGuards";
import PastCallChats from "./PastCallChats";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtDuration(start, end) {
  if (!start || !end) return "";
  const mins = Math.round((new Date(end) - new Date(start)) / 60000);
  if (mins < 60) return `${mins} min`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

const STATUS_LABELS = {
  waiting: { label: "Waiting for match", color: "var(--gold-bright)", bg: "var(--gold-bg)" },
  matched_pending_confirmation: {
    label: "Match proposed",
    color: "var(--tag-teal)", bg: "var(--tag-teal-bg)",
  },
  scheduled: { label: "Scheduled", color: "var(--tag-teal)", bg: "var(--tag-teal-bg)" },
  in_call: { label: "In Call", color: "var(--tag-rose)", bg: "var(--tag-rose-bg)" },
  cancelled: { label: "Cancelled", color: "var(--parchment-dim)", bg: "var(--slate-bg)" },
};

const SESSION_STATUS_LABELS = {
  proposed: { label: "Match Proposed", color: "var(--tag-violet)", bg: "var(--tag-violet-bg)" },
  confirmed: { label: "Confirmed", color: "var(--tag-teal)", bg: "var(--tag-teal-bg)" },
  ready: { label: "Ready to Join!", color: "var(--gold-bright)", bg: "var(--gold-bg)" },
  active: { label: "In Progress", color: "var(--tag-rose)", bg: "var(--tag-rose-bg)" },
  completed: { label: "Completed", color: "var(--parchment-dim)", bg: "var(--slate-bg)" },
  cancelled: { label: "Cancelled", color: "var(--parchment-dim)", bg: "var(--slate-bg)" },
};

function StatusBadge({ status, map }) {
  const info = (map || STATUS_LABELS)[status] ||
    { label: status, color: "var(--parchment-dim)", bg: "var(--slate-bg)" };
  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border"
      style={{ color: info.color, background: info.bg, borderColor: info.color }}
    >
      {info.label}
    </span>
  );
}

// ─── Open-lobby UI helpers ────────────────────────────────────────────────────

const BAND_STYLES = {
  beginner: { label: "BEGINNER", color: "var(--tag-teal)", bg: "var(--tag-teal-bg)" },
  intermediate: { label: "INTERMEDIATE", color: "var(--gold-bright)", bg: "var(--gold-bg)" },
  advanced: { label: "ADVANCED", color: "var(--tag-rose)", bg: "var(--tag-rose-bg)" },
  any: { label: "ANY LEVEL", color: "var(--parchment-dim)", bg: "var(--slate-bg)" },
};

function BandBadge({ band }) {
  const s = BAND_STYLES[band] || BAND_STYLES.any;
  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold tracking-widest border"
      style={{ color: s.color, borderColor: s.color, background: s.bg }}
    >
      {s.label}
    </span>
  );
}

function LockIcon({ className = "w-3.5 h-3.5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
    </svg>
  );
}

// Local calendar days starting today (used by both the browse + create screens)
function nextDays(count) {
  return Array.from({ length: count }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    d.setHours(0, 0, 0, 0);
    return d;
  });
}

function sameLocalDay(iso, day) {
  const d = new Date(iso);
  return (
    d.getFullYear() === day.getFullYear() &&
    d.getMonth() === day.getMonth() &&
    d.getDate() === day.getDate()
  );
}

function fmtClock(iso) {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
}

function fmtUntil(iso) {
  const diffMs = new Date(iso) - Date.now();
  if (diffMs <= 0) return "live now"; // slot already started — a spot re-opened mid-call
  const mins = Math.round(diffMs / 60000);
  if (mins < 60) return `in ${mins} min`;
  const h = Math.floor(mins / 60);
  return `in ${h}h ${mins % 60}m`;
}

function DateChips({ count, selectedIdx, onSelect }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1">
      {nextDays(count).map((d, i) => {
        const selected = i === selectedIdx;
        return (
          <button
            key={i}
            onClick={() => onSelect(i)}
            className={`flex flex-col items-center justify-center w-[4.2rem] h-[4.2rem] shrink-0 rounded-2xl transition-colors ${
              selected
                ? "bg-dt-gold-bright text-dt-ink"
                : "surface-glass surface--interactive text-dt-parchment-dim"
            }`}
          >
            <span className={`text-xs ${selected ? "font-medium" : ""}`}>
              {i === 0 ? "Today" : d.toLocaleDateString(undefined, { weekday: "short" })}
            </span>
            <span className="text-xl font-bold mt-0.5">{d.getDate()}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Create-lobby screen ──────────────────────────────────────────────────────

const DURATION_OPTIONS = [15, 30, 45, 60];

function CreateLobbyScreen({ onBack, onCreate, creating, error, durationOptions, maxParticipants = 2, institute = null }) {
  // Admin controls which slot lengths are offered; fall back to the full set.
  const durations =
    Array.isArray(durationOptions) && durationOptions.length ? durationOptions : DURATION_OPTIONS;
  const tzName = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const [dayIdx, setDayIdx] = useState(0);
  const [startMin, setStartMin] = useState(null); // minutes from local midnight
  const [duration, setDuration] = useState(durations[0]);
  // Call size — host picks 2..maxParticipants (admin-controlled ceiling).
  const [capacity, setCapacity] = useState(2);
  const sizeOptions = [];
  for (let n = 2; n <= Math.max(2, maxParticipants); n += 1) sizeOptions.push(n);
  const [strict, setStrict] = useState(false);
  // Institute members may restrict their lobby to their own institute.
  const canInstituteOnly = institute?.status === "active";
  const [visibility, setVisibility] = useState("public"); // "public" | "institute"
  // Keep the selected duration valid if the allowed set changes.
  useEffect(() => {
    if (!durations.includes(duration)) setDuration(durations[0]);
  }, [durations, duration]);
  // Re-render every 30s so today's already-passed time chips drop off the list
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const day = nextDays(dayIdx + 1)[dayIdx];
  const now = new Date();
  const firstMin =
    dayIdx === 0 ? Math.ceil((now.getHours() * 60 + now.getMinutes() + 1) / 15) * 15 : 0;
  const times = [];
  for (let m = firstMin; m < 24 * 60; m += 15) times.push(m);
  // The chosen start, falling back to the first future slot of the day
  const effStart = startMin != null && times.includes(startMin) ? startMin : times[0];

  const fmtMin = (m) =>
    `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

  const startDate =
    effStart != null
      ? new Date(day.getFullYear(), day.getMonth(), day.getDate(), Math.floor(effStart / 60), effStart % 60)
      : null;
  const endDate = startDate ? new Date(startDate.getTime() + duration * 60000) : null;
  const dayLabel =
    dayIdx === 0 ? "Today" : day.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <button
          onClick={onBack}
          aria-label="Back to open lobbies"
          className="surface-glass w-11 h-11 rounded-xl flex items-center justify-center text-dt-parchment hover:text-dt-gold-bright shrink-0"
        >
          ←
        </button>
        <div>
          <h2 className="text-2xl font-bold text-dt-parchment">Start a lobby</h2>
          <p className="text-sm text-dt-parchment-dim mt-0.5">We'll match you with a student at your level.</p>
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold tracking-widest uppercase text-dt-parchment-dim mb-2">Date</p>
        <DateChips count={6} selectedIdx={dayIdx} onSelect={(i) => { setDayIdx(i); setStartMin(null); }} />
      </div>

      <div>
        <p className="text-xs font-semibold tracking-widest uppercase text-dt-parchment-dim mb-2">Start time</p>
        {times.length === 0 ? (
          <p className="text-sm text-dt-parchment-dim">No more slots today — pick another day.</p>
        ) : (
          <div className="flex gap-2 overflow-x-auto pb-2 scroll-elegant">
            {times.map((m) => (
              <button
                key={m}
                onClick={() => setStartMin(m)}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium shrink-0 transition-colors ${
                  m === effStart
                    ? "bg-dt-gold-bright text-dt-ink"
                    : "surface-glass surface--interactive text-dt-parchment"
                }`}
              >
                {fmtMin(m)}
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="text-xs font-semibold tracking-widest uppercase text-dt-parchment-dim mb-2">Duration</p>
        <div className={`grid gap-2 ${durations.length >= 4 ? "grid-cols-4" : durations.length === 3 ? "grid-cols-3" : durations.length === 2 ? "grid-cols-2" : "grid-cols-1"}`}>
          {durations.map((d) => (
            <button
              key={d}
              onClick={() => setDuration(d)}
              className={`py-2.5 rounded-xl text-sm font-medium transition-colors ${
                d === duration
                  ? "bg-dt-gold-bright text-dt-ink"
                  : "surface-glass surface--interactive text-dt-parchment"
              }`}
            >
              {d} min
            </button>
          ))}
        </div>
      </div>

      {startDate && (
        <div className="surface-glass rounded-2xl p-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold tracking-widest uppercase" style={{ color: "var(--tag-teal)" }}>
              Your slot
            </p>
            <p className="text-lg font-bold text-dt-parchment mt-0.5">
              {dayLabel} · {fmtMin(effStart)} – {fmtMin((effStart + duration) % (24 * 60))}
            </p>
          </div>
          <span className="text-sm text-dt-parchment-dim shrink-0">{duration} min</span>
        </div>
      )}

      {sizeOptions.length > 1 && (
        <div>
          <p className="text-xs font-semibold tracking-widest uppercase text-dt-parchment-dim mb-2">Call size</p>
          <div className={`grid gap-2 ${sizeOptions.length >= 4 ? "grid-cols-4" : sizeOptions.length === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
            {sizeOptions.map((n) => (
              <button
                key={n}
                onClick={() => setCapacity(n)}
                className={`py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  n === capacity
                    ? "bg-dt-gold-bright text-dt-ink"
                    : "surface-glass surface--interactive text-dt-parchment"
                }`}
              >
                {n === 2 ? "1-to-1" : `${n} people`}
              </button>
            ))}
          </div>
          {capacity > 2 && (
            <p className="text-xs text-dt-parchment-dim mt-1.5">
              The call starts at the scheduled time with everyone who has
              joined (at least 2) — latecomers can still hop in until it ends.
            </p>
          )}
        </div>
      )}

      <label className="flex items-start gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={strict}
          onChange={(e) => setStrict(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded accent-[var(--gold)]"
        />
        <span className="text-sm text-dt-parchment">
          Only match me with students at my level
          <span className="block text-xs text-dt-parchment-dim mt-0.5">
            Strict lobbies show a lock and only accept students in your level band.
          </span>
        </span>
      </label>

      {canInstituteOnly && (
        <div>
          <p className="text-xs font-semibold tracking-widest uppercase text-dt-parchment-dim mb-2">
            Who can join
          </p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { value: "public", label: "Everyone" },
              { value: "institute", label: `${institute?.name || "My institute"} only` },
            ].map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => setVisibility(value)}
                className={`py-2.5 px-3 rounded-xl text-sm font-medium truncate transition-colors ${
                  visibility === value
                    ? "bg-dt-gold-bright text-dt-ink"
                    : "surface-glass surface--interactive text-dt-parchment"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {visibility === "institute" && (
            <p className="text-xs text-dt-parchment-dim mt-1.5">
              Only students from your institute will see this lobby. Gender rules still apply.
            </p>
          )}
        </div>
      )}

      <p className="text-sm text-dt-parchment-dim flex items-start gap-2">
        <LockIcon className="w-4 h-4 mt-0.5 shrink-0 text-dt-gold-dim" />
        <span>
          You'll be matched with a student of the same level and gender. Times shown in{" "}
          <strong className="text-dt-parchment">{tzName}</strong>.
        </span>
      </p>

      {error && (
        <p className="text-sm rounded-xl px-3 py-2" style={{ color: "var(--tag-rose)", background: "var(--tag-rose-bg)" }}>
          {error}
        </p>
      )}

      <button
        disabled={creating || !startDate}
        onClick={() =>
          onCreate({
            start_utc: startDate.toISOString(),
            end_utc: endDate.toISOString(),
            strict_level_matching: strict,
            timezone: tzName,
            visibility: canInstituteOnly ? visibility : "public",
            capacity,
          })
        }
        className="w-full py-3.5 rounded-2xl text-base font-semibold bg-dt-gold-bright text-dt-ink hover:brightness-105 disabled:opacity-50 transition-[filter]"
      >
        {creating ? "Creating…" : "Create this lobby"}
      </button>
    </div>
  );
}

// ─── Call Room (LiveKit) ──────────────────────────────────────────────────────

function Spinner() {
  return (
    <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}

// Pick a recording container the browser actually supports. Chrome → webm/opus;
// iOS Safari only supports mp4. Empty string = let the browser choose its default.
function pickRecorderMime() {
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/aac"];
  const supported = typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported;
  for (const m of candidates) {
    if (supported && MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
}

// ─── Video tile ───────────────────────────────────────────────────────────────
// One participant in the call grid. Attaches/detaches the LiveKit track itself
// so tiles can mount, unmount and reorder freely (2 today, up to 4 later).
function VideoTile({ track, label = "", you = false, active = false, micOff = false, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !track) return;
    track.attach(el);
    return () => {
      try { track.detach(el); } catch (_) {}
    };
  }, [track]);
  const initial = (label || "?").trim().charAt(0).toUpperCase();
  return (
    <div
      className={`relative overflow-hidden rounded-2xl ${className}`}
      style={{
        background: "var(--forest)",
        border: `1px solid ${active ? "var(--gold)" : "var(--panel-line)"}`,
        boxShadow: active ? "0 0 0 1px rgba(201, 162, 39, 0.35)" : "none",
      }}
    >
      <video ref={ref} autoPlay playsInline muted={you} className={`w-full h-full object-cover ${track ? "" : "hidden"}`} />
      {!track && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-lg font-bold select-none"
            style={{ background: "var(--forest-softer)", color: "var(--parchment)", border: "1px solid var(--panel-line)" }}
          >
            {initial}
          </span>
        </div>
      )}
      {label && (
        <span
          className="absolute bottom-1.5 right-2 max-w-[85%] truncate text-[11px] font-medium px-2 py-0.5 rounded-full"
          style={{ background: "rgba(6, 16, 12, 0.55)", color: "var(--parchment)" }}
        >
          {label}
        </span>
      )}
      {micOff && (
        <span
          className="absolute bottom-1.5 left-2 w-5 h-5 rounded-full flex items-center justify-center"
          style={{ background: "rgba(6, 16, 12, 0.55)", color: "var(--tag-rose)" }}
          title="Microphone muted"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3zM3 3l18 18" />
          </svg>
        </span>
      )}
    </div>
  );
}

function CallRoom({ sessionId, onLeave, onRejoin, minimized = false, scheduledEnd = null,
                    preCallDone = false, initialMic = true, initialCamera = true, onPreCallDone,
                    onOpenSpeakingHelper, onChatChange, myName = "", partnerName = "",
                    groupCall = false, initialMessages = [] }) {
  const [callInfo, setCallInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [disconnected, setDisconnected] = useState(false);
  // Pre-call config screen — skip if already passed once (e.g. after minimize/maximize)
  const [preCall, setPreCall] = useState(!preCallDone);
  // Local video in state — attached by <VideoTile> after DOM renders.
  const [localVideoTrack, setLocalVideoTrack] = useState(null);
  // Remote participants keyed by LiveKit identity → { videoTrack, name }.
  // The map drives the tile grid for 1-to-1 and group calls alike.
  const [remotePeers, setRemotePeers] = useState({});
  const [activeIds, setActiveIds] = useState(() => new Set()); // active speakers
  // Media toggle state — seeded from parent so saved prefs survive remounts
  const [micEnabled, setMicEnabled] = useState(initialMic);
  const [cameraEnabled, setCameraEnabled] = useState(initialCamera);
  // md breakpoint — the tile layouts use dynamic grid templates that CSS
  // breakpoint classes can't switch, so branch in JS instead.
  const [isMdUp, setIsMdUp] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = (e) => setIsMdUp(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  // Countdown
  const [secsLeft, setSecsLeft] = useState(null);
  const [timeWarning, setTimeWarning] = useState(false);
  const roomRef = useRef(null);
  // Set to true only when user explicitly clicks "End Session" — not on unexpected disconnect
  const intentionalLeaveRef = useRef(false);
  const micEnabledRef = useRef(initialMic);

  // ── Live transcript ────────────────────────────────────────────────────────
  // When the admin flag is on, the learner can turn on "Transcript": a Web
  // Audio analyser does client-side voice-activity detection (VAD) on the
  // learner's OWN microphone. Each finished utterance is transcribed into a
  // DRAFT the learner reviews (edit / redo / translate) and then sends. Sent
  // lines are shared with everyone on the call over the LiveKit data channel,
  // so each side sees the other's verified words. Silence costs nothing.
  const VAD_THRESHOLD = 0.018;     // RMS (0–1) above which a frame counts as speech
  const VAD_SILENCE_MS = 800;      // trailing silence that ends an utterance
  const VAD_MIN_SPEECH_MS = 350;   // ignore blips shorter than this
  const VAD_MAX_SPEECH_MS = 15000; // force-close a runaway utterance
  const VAD_TICK_MS = 60;          // analyser poll cadence
  const [autoAvailable, setAutoAvailable] = useState(false);
  const [listening, setListening] = useState(false);   // Transcript feature on
  const [speaking, setSpeaking] = useState(false);     // currently voicing an utterance
  const [transcribing, setTranscribing] = useState(false);
  const [vadPaused, setVadPaused] = useState(false);   // tab hidden / audio ctx suspended
  const [limitReached, setLimitReached] = useState(false); // daily AI-help cap hit
  const [showHarakat, setShowHarakat] = useState(true);
  // The reviewable draft of what I just said
  const [draft, setDraft] = useState("");
  const [draftTranslation, setDraftTranslation] = useState("");
  const [draftBusy, setDraftBusy] = useState(false);   // translate-in-flight
  // Reply suggestions — generated ONLY when the user taps a partner bubble
  const [suggestions, setSuggestions] = useState([]);
  const [suggestBusy, setSuggestBusy] = useState(false);
  // Chat transcript: everyone's verified lines. Survives minimize/maximize via parent.
  const [messages, setMessages] = useState(() => initialMessages || []);
  const messagesRef = useRef(initialMessages || []);
  const chatSaveTimerRef = useRef(null);
  const localAudioTrackRef = useRef(null);
  const lastAudioRef = useRef(null);   // last recorded utterance blob (for Play)
  const listeningRef = useRef(false);
  const inFlightRef = useRef(false);
  const audioCtxRef = useRef(null);
  const vadTimerRef = useRef(null);
  const recorderRef = useRef(null);
  const visHandlerRef = useRef(null);

  // Is the feature flag on? (admin-controlled via SiteConfig.auto_suggest_enabled)
  useEffect(() => {
    let cancelled = false;
    getUsageLimits()
      .then((l) => { if (!cancelled) setAutoAvailable(!!l?.auto_suggest_enabled); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Append a chat message, notify the parent (for the post-call review), and
  // persist the transcript server-side (debounced).
  const appendMessage = useCallback((msg) => {
    setMessages((prev) => {
      const next = [...prev, msg];
      messagesRef.current = next;
      onChatChange?.(next);
      if (chatSaveTimerRef.current) clearTimeout(chatSaveTimerRef.current);
      if (sessionId) {
        chatSaveTimerRef.current = setTimeout(() => {
          savePracticeCallChat(sessionId, next).catch(() => {});
        }, 1200);
      }
      return next;
    });
  }, [onChatChange, sessionId]);

  // Share a sent line with everyone on the call (LiveKit data channel).
  const publishChat = useCallback((msg) => {
    const room = roomRef.current;
    if (!room) return;
    try {
      const payload = new TextEncoder().encode(JSON.stringify({
        t: "chat", name: msg.name, arabic: msg.arabic, translation: msg.translation || "", ts: msg.ts,
      }));
      room.localParticipant.publishData(payload, { reliable: true, topic: "chat" });
    } catch { /* best-effort — the line still shows locally */ }
  }, []);

  // Ask the AI for fuṣḥā replies to a partner's line — fired ONLY from a bubble tap.
  const fireAutoSuggest = useCallback(async (text) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setSuggestBusy(true);
    setSuggestions([]);
    try {
      const data = await autoSuggestReplies(text);
      setSuggestions(data.replies || []);
    } catch (e) {
      if (e.status === 403) setAutoAvailable(false); // flag turned off server-side
      if (e.status === 429) setLimitReached(true);   // daily cap hit
    } finally {
      inFlightRef.current = false;
      setSuggestBusy(false);
    }
  }, []);

  // User picked a suggestion → it becomes their draft to review & send.
  const pickSuggestion = useCallback((r) => {
    setDraft(r.arabic);
    setDraftTranslation(r.translation || "");
    lastAudioRef.current = null;
    setSuggestions([]);
  }, []);

  // AI-correct a chat line in place (fix STT, add harakat + translation).
  const correctMessage = useCallback(async (index, msg) => {
    const { arabic, translation } = await correctTranscript(msg.arabic);
    setMessages((prev) => {
      const next = prev.map((m, i) => (i === index ? { ...m, arabic, translation: translation || m.translation } : m));
      messagesRef.current = next;
      onChatChange?.(next);
      if (chatSaveTimerRef.current) clearTimeout(chatSaveTimerRef.current);
      if (sessionId) {
        chatSaveTimerRef.current = setTimeout(() => savePracticeCallChat(sessionId, next).catch(() => {}), 1200);
      }
      return next;
    });
  }, [onChatChange, sessionId]);

  // Transcribe a finished utterance of MY speech → append to the draft.
  const handleMyUtterance = useCallback(async (blob, durationSeconds) => {
    if (!listeningRef.current || blob.size < 1500) return;
    // Whisper hallucinates on near-silent audio ("subscribe to the channel"…).
    // Same double guard as Free Chat: RMS check before paying for STT, then a
    // known-hallucination blocklist on the transcript.
    if (!(await hasSpeechContent(blob))) return;
    lastAudioRef.current = blob;
    setTranscribing(true);
    try {
      const { text } = await transcribeSpeech(blob, durationSeconds);
      const t = (text || "").trim();
      if (t && !isWhisperHallucination(t)) setDraft((d) => (d ? `${d} ${t}` : t));
    } catch { /* transient STT error — skip */ }
    finally { setTranscribing(false); }
  }, []);

  // Start the VAD engine on my own microphone and emit utterances.
  const startVad = useCallback(() => {
    const mst = localAudioTrackRef.current?.mediaStreamTrack;
    if (!mst) return false;
    let ctx, analyser;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctx = new AC();
      analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      ctx.createMediaStreamSource(new MediaStream([mst])).connect(analyser); // not wired to output → no echo
      ctx.resume?.().catch(() => {});
    } catch {
      return false;
    }
    audioCtxRef.current = ctx;
    const stream = new MediaStream([mst]);
    const buf = new Uint8Array(analyser.fftSize);
    const mime = pickRecorderMime();

    // Pause cleanly when the tab is hidden / screen locks (mobile suspends the
    // AudioContext); resume when the call returns to the foreground.
    const onVisibility = () => {
      if (document.hidden) {
        setVadPaused(true);
      } else {
        audioCtxRef.current?.resume?.().catch(() => {});
        setVadPaused(false);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    visHandlerRef.current = onVisibility;

    let recording = false;
    let chunks = [];
    let speechStart = 0;
    let lastVoice = 0;

    const endUtterance = () => {
      recording = false;
      setSpeaking(false);
      const r = recorderRef.current;
      if (r && r.state !== "inactive") { try { r.stop(); } catch { /* noop */ } }
    };
    const dropUtterance = () => {
      recording = false;
      setSpeaking(false);
      const r = recorderRef.current;
      if (r && r.state !== "inactive") { try { r.onstop = null; r.stop(); } catch { /* noop */ } }
      chunks = [];
    };

    const tick = () => {
      if (!listeningRef.current) return;
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / buf.length);
      const now = performance.now();
      const muted = !micEnabledRef.current || mst.muted;
      const voiced = !muted && rms >= VAD_THRESHOLD;

      if (voiced) {
        lastVoice = now;
        if (!recording) {
          recording = true;
          speechStart = now;
          chunks = [];
          setSpeaking(true);
          try {
            const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
            recorderRef.current = rec;
            rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
            rec.onstop = () => handleMyUtterance(
              new Blob(chunks, { type: chunks[0]?.type || mime || "audio/webm" }),
              (performance.now() - speechStart) / 1000,
            );
            rec.start();
          } catch { recording = false; setSpeaking(false); }
        }
      } else if (recording) {
        const tooLong = now - speechStart >= VAD_MAX_SPEECH_MS;
        const silentEnough = now - lastVoice >= VAD_SILENCE_MS;
        if (tooLong || silentEnough) {
          // Speech duration excludes the trailing silence window.
          if (!tooLong && (lastVoice - speechStart) < VAD_MIN_SPEECH_MS) dropUtterance();
          else endUtterance();
        }
      }
      vadTimerRef.current = setTimeout(tick, VAD_TICK_MS);
    };
    tick();
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleMyUtterance]);

  function stopListening() {
    listeningRef.current = false;
    setListening(false);
    setSpeaking(false);
    setVadPaused(false);
    if (vadTimerRef.current) { clearTimeout(vadTimerRef.current); vadTimerRef.current = null; }
    if (visHandlerRef.current) {
      document.removeEventListener("visibilitychange", visHandlerRef.current);
      visHandlerRef.current = null;
    }
    const r = recorderRef.current;
    if (r && r.state !== "inactive") { try { r.onstop = null; r.stop(); } catch { /* noop */ } }
    recorderRef.current = null;
    const ctx = audioCtxRef.current;
    if (ctx && ctx.state !== "closed") { try { ctx.close(); } catch { /* noop */ } }
    audioCtxRef.current = null;
  }

  const toggleListening = useCallback(() => {
    if (listeningRef.current) { stopListening(); return; }
    if (!localAudioTrackRef.current) return; // mic not published yet
    setLimitReached(false);
    listeningRef.current = true;
    setListening(true);
    if (!startVad()) stopListening(); // couldn't init Web Audio — revert
  }, [startVad]);

  // Tear down on unmount.
  useEffect(() => () => stopListening(), []);

  // ── Draft actions ─────────────────────────────────────────────────────────
  const redoDraft = () => {
    setDraft("");
    setDraftTranslation("");
    lastAudioRef.current = null;
  };

  const playDraft = () => {
    const blob = lastAudioRef.current;
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    audio.onended = () => URL.revokeObjectURL(url);
    audio.play().catch(() => URL.revokeObjectURL(url));
  };

  const translateDraft = async () => {
    const text = draft.trim();
    if (!text || draftBusy) return;
    setDraftBusy(true);
    try {
      const { arabic, translation } = await correctTranscript(text);
      setDraft(arabic || text);
      setDraftTranslation(translation || "");
    } catch (e) {
      if (e.status === 429) setLimitReached(true);
    } finally {
      setDraftBusy(false);
    }
  };

  const sendDraft = () => {
    const text = draft.trim();
    if (!text) return;
    const msg = { role: "me", name: myName || "You", arabic: text, translation: draftTranslation || "", ts: Date.now() };
    appendMessage(msg);
    publishChat(msg);
    redoDraft();
  };

  // Countdown timer — counts down to scheduledEnd, warns at 60s, auto-ends at 0
  useEffect(() => {
    if (!scheduledEnd) return;
    const endMs = new Date(scheduledEnd).getTime();

    const tick = () => {
      const s = Math.round((endMs - Date.now()) / 1000);
      setSecsLeft(s);
      if (s <= 60 && s > 0) setTimeWarning(true);
      if (s <= 0) {
        // Time's up — end session automatically
        intentionalLeaveRef.current = true;
        practiceSessionComplete(sessionId).catch(() => {});
        if (roomRef.current) roomRef.current.disconnect();
        onLeave(partnerName);
      }
    };

    tick(); // run immediately
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [scheduledEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (preCall) return; // wait for user to confirm media settings
    let cancelled = false;
    intentionalLeaveRef.current = false;

    async function initCall() {
      try {
        const info = await practiceSessionJoin(sessionId);
        if (cancelled) return;
        setCallInfo(info);
        await connectLiveKit(info, () => cancelled);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    initCall();
    return () => {
      cancelled = true;
      if (roomRef.current) {
        roomRef.current.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, preCall]);

  async function connectLiveKit(info, isCancelled) {
    if (!info.livekit_configured || !info.livekit_url || info.token === "livekit-not-configured-testing-only") {
      setError("__testing_mode__");
      return;
    }

    try {
      const { Room, RoomEvent, Track, createLocalTracks } = await import("livekit-client");

      const room = new Room({ adaptiveStream: true, dynacast: true });
      roomRef.current = room;

      const upsertPeer = (identity, patch) =>
        setRemotePeers((prev) => ({ ...prev, [identity]: { videoTrack: null, name: "", ...prev[identity], ...patch } }));

      room.on(RoomEvent.ParticipantConnected, (p) => upsertPeer(p.identity, { name: p.name || "" }));
      room.on(RoomEvent.ParticipantDisconnected, (p) =>
        setRemotePeers((prev) => {
          const next = { ...prev };
          delete next[p.identity];
          return next;
        })
      );

      room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
        if (track.kind === Track.Kind.Video) {
          upsertPeer(participant.identity, { videoTrack: track, name: participant.name || "" });
        } else if (track.kind === Track.Kind.Audio) {
          track.attach(); // creates and auto-appends an <audio> element
          upsertPeer(participant.identity, { name: participant.name || "" });
        }
      });

      room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
        if (track.kind === Track.Kind.Video && participant) {
          setRemotePeers((prev) =>
            prev[participant.identity] ? { ...prev, [participant.identity]: { ...prev[participant.identity], videoTrack: null } } : prev
          );
        }
        track.detach();
      });

      // Gold ring on whoever is talking (matches the mock's highlighted tile).
      room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
        setActiveIds(new Set(speakers.map((s) => s.identity)));
      });

      // A partner sent a verified transcript line — add it to the shared chat.
      room.on(RoomEvent.DataReceived, (payload, participant, _kind, topic) => {
        if (topic && topic !== "chat") return;
        let msg;
        try { msg = JSON.parse(new TextDecoder().decode(payload)); } catch { return; }
        if (!msg || msg.t !== "chat" || !msg.arabic) return;
        appendMessage({
          role: "partner",
          name: msg.name || "",
          arabic: msg.arabic,
          translation: msg.translation || "",
          ts: msg.ts || Date.now(),
        });
        if (participant && msg.name) upsertPeer(participant.identity, { name: msg.name });
      });

      room.on(RoomEvent.Disconnected, () => {
        if (isCancelled()) return; // component unmounting — ignore
        if (intentionalLeaveRef.current) {
          // Intentional leave — complete() already called in handleLeave
          onLeave(partnerName);
        } else {
          // Unexpected disconnect — show rejoin UI; keep session active
          setLoading(false);
          setDisconnected(true);
        }
      });

      const wsUrl = info.livekit_url
        .replace(/^https:\/\//, "wss://")
        .replace(/^http:\/\//, "ws://");
      await room.connect(wsUrl, info.token);

      if (isCancelled()) return;

      const tracks = await createLocalTracks({ audio: true, video: true });
      for (const track of tracks) {
        await room.localParticipant.publishTrack(track);
        if (track.kind === Track.Kind.Video) setLocalVideoTrack(track);
        if (track.kind === Track.Kind.Audio) localAudioTrackRef.current = track;
      }
      // Apply pre-call media preferences immediately after publishing
      await room.localParticipant.setMicrophoneEnabled(micEnabled).catch(() => {});
      await room.localParticipant.setCameraEnabled(cameraEnabled).catch(() => {});

      // Catch any remote participants/tracks that arrived before our listeners
      for (const participant of room.remoteParticipants.values()) {
        upsertPeer(participant.identity, { name: participant.name || "" });
        for (const pub of participant.trackPublications.values()) {
          if (!pub.isSubscribed || !pub.track) continue;
          if (pub.track.kind === Track.Kind.Video) {
            upsertPeer(participant.identity, { videoTrack: pub.track });
          } else if (pub.track.kind === Track.Kind.Audio) {
            pub.track.attach();
          }
        }
      }
    } catch (e) {
      console.error("LiveKit connect error:", e);
      setError("Could not connect to call room. Please check that LiveKit is configured.");
    }
  }

  async function handleLeave() {
    // Mark session completed — this is the permanent "end session" action
    intentionalLeaveRef.current = true;
    // Flush any pending chat save so the transcript isn't lost on exit.
    if (chatSaveTimerRef.current) clearTimeout(chatSaveTimerRef.current);
    if (sessionId && messagesRef.current.length) {
      await savePracticeCallChat(sessionId, messagesRef.current).catch(() => {});
    }
    await practiceSessionComplete(sessionId).catch(() => {});
    if (roomRef.current) roomRef.current.disconnect();
    onLeave(partnerName || callInfo?.partner_username);
  }

  async function toggleMic() {
    const room = roomRef.current;
    if (!room) return;
    const enabled = !micEnabled;
    await room.localParticipant.setMicrophoneEnabled(enabled).catch(() => {});
    micEnabledRef.current = enabled;
    setMicEnabled(enabled);
  }

  async function toggleCamera() {
    const room = roomRef.current;
    if (!room) return;
    const enabled = !cameraEnabled;
    await room.localParticipant.setCameraEnabled(enabled).catch(() => {});
    setCameraEnabled(enabled);
  }

  // ── Countdown display helper ──────────────────────────────────────────────
  function CountdownBadge({ compact = false }) {
    if (secsLeft === null || secsLeft < 0) return null;
    const mins = Math.floor(secsLeft / 60);
    const secs = secsLeft % 60;
    const label = `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    const isWarn = secsLeft <= 60;
    if (compact) {
      return (
        <span className={`text-xs font-mono font-semibold px-1.5 py-0.5 rounded ${isWarn ? "bg-rose-600 text-white animate-pulse" : ""}`}
          style={isWarn ? undefined : { background: "var(--forest)", color: "var(--parchment-dim)" }}>
          {label}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-mono font-semibold"
        style={{ color: isWarn ? "var(--tag-rose)" : "var(--parchment-dim)" }}>
        Live · {label}
        <span className={`w-1.5 h-1.5 rounded-full ${isWarn ? "animate-pulse" : ""}`} style={{ background: "var(--tag-rose)" }} />
      </span>
    );
  }

  // ── Shared icon helpers ───────────────────────────────────────────────────
  const MicIcon = () => micEnabled ? (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
    </svg>
  ) : (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3zM3 3l18 18" />
    </svg>
  );

  const CamIcon = () => cameraEnabled ? (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
    </svg>
  ) : (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25zM3 3l18 18" />
    </svg>
  );

  // Error screens
  if (preCall) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[320px] p-6">
        <div className="surface-glass rounded-2xl shadow-sm p-6 w-full max-w-xs space-y-5">
          <div className="text-center">
            <h3 className="font-semibold text-slate-800 mb-1">Ready to join?</h3>
            <p className="text-xs text-slate-500">Configure your mic and camera before joining the call.</p>
          </div>

          {/* Mic toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MicIcon />
              <span className="text-sm text-slate-700 font-medium">Microphone</span>
            </div>
            <button
              onClick={() => setMicEnabled((v) => { micEnabledRef.current = !v; return !v; })}
              className={`relative inline-flex h-6 w-11 rounded-full transition-colors focus:outline-none ${micEnabled ? "bg-emerald-500" : "bg-slate-300"}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${micEnabled ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </div>
          <p className="text-xs text-center -mt-3 text-slate-400">
            {micEnabled ? "Mic on — you will be heard" : "Mic off — you will join muted"}
          </p>

          {/* Camera toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CamIcon />
              <span className="text-sm text-slate-700 font-medium">Camera</span>
            </div>
            <button
              onClick={() => setCameraEnabled((v) => !v)}
              className={`relative inline-flex h-6 w-11 rounded-full transition-colors focus:outline-none ${cameraEnabled ? "bg-emerald-500" : "bg-slate-300"}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${cameraEnabled ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </div>
          <p className="text-xs text-center -mt-3 text-slate-400">
            {cameraEnabled ? "Camera on — your video will be shared" : "Camera off — you will join without video"}
          </p>

          <div className="flex gap-2 pt-1">
            <button
              onClick={onLeave}
              className="flex-1 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => { onPreCallDone?.(micEnabled, cameraEnabled); setPreCall(false); }}
              className="flex-1 py-2 rounded-xl bg-dt-gold-bright text-dt-ink text-sm font-semibold hover:brightness-105 transition-[filter]"
            >
              Join Call
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error === "__testing_mode__") {
    return (
      <div className="rounded-xl bg-amber-50 border border-amber-200 p-6 text-center">
        <p className="text-amber-800 font-medium mb-2">Testing Mode</p>
        <p className="text-amber-700 text-sm mb-4">LiveKit is not configured. Video call unavailable.</p>
        <button onClick={onLeave} className="px-4 py-2 bg-slate-700 text-white rounded-lg text-sm hover:bg-slate-800">Back</button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl bg-rose-50 border border-rose-200 p-6 text-center">
        <p className="text-rose-700 text-sm mb-4">{error}</p>
        <button onClick={onLeave} className="px-4 py-2 bg-slate-700 text-white rounded-lg text-sm hover:bg-slate-800">Back</button>
      </div>
    );
  }

  if (disconnected) {
    if (minimized) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 p-3 text-center" style={{ background: "var(--ink)" }}>
          <p className="text-xs font-medium" style={{ color: "var(--parchment-dim)" }}>Disconnected</p>
          <div className="flex gap-2">
            <button onClick={onRejoin} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full text-xs font-medium">Rejoin</button>
            <button onClick={handleLeave} className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-full text-xs font-medium">End</button>
          </div>
        </div>
      );
    }
    return (
      <div className="surface-glass rounded-xl p-8 text-center space-y-4">
        <p className="font-medium" style={{ color: "var(--parchment)" }}>You were disconnected</p>
        <p className="text-sm" style={{ color: "var(--parchment-dim)" }}>The session is still active. You can rejoin as long as the time slot hasn't expired.</p>
        <div className="flex justify-center gap-3 pt-2">
          <button onClick={onRejoin} className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full text-sm font-medium transition-colors">Rejoin Call</button>
          <button onClick={handleLeave} className="px-5 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-full text-sm font-medium transition-colors">Leave Call</button>
        </div>
      </div>
    );
  }

  const peers = Object.entries(remotePeers); // [identity, { videoTrack, name }]
  const firstRemote = peers[0]?.[1] || null;
  const myIdentity = roomRef.current?.localParticipant?.identity;

  // ── Minimized (PiP floating widget) ──────────────────────────────────────
  if (minimized) {
    return (
      <div style={{ display: loading ? "none" : "flex" }} className="flex-col">
        {/* Partner video — full widget width */}
        <div className="relative aspect-video w-full overflow-hidden" style={{ background: "var(--forest)" }}>
          <VideoTile
            track={firstRemote?.videoTrack || null}
            label={firstRemote ? firstRemote.name || partnerName || "Partner" : ""}
            className="absolute inset-0 rounded-none"
          />
          {!firstRemote && (
            <div className="absolute inset-0 flex items-center justify-center text-xs" style={{ color: "var(--parchment-dim)" }}>
              {groupCall ? "Waiting for others…" : "Waiting for partner…"}
            </div>
          )}
          {/* Self-view PiP — bottom-right corner */}
          <div className="absolute bottom-1.5 right-1.5 w-16 aspect-video z-10">
            <VideoTile track={cameraEnabled ? localVideoTrack : null} label="" you className="w-full h-full rounded-lg" />
          </div>
          {/* Live indicator */}
          <div className="absolute top-1.5 left-1.5 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
            <span className="text-white text-xs bg-black/50 px-1 py-0.5 rounded">Live</span>
          </div>
          {/* More participants than the PiP can show */}
          {peers.length > 1 && (
            <div className="absolute top-1.5 right-1.5 z-10">
              <span className="text-white text-xs bg-black/50 px-1.5 py-0.5 rounded-full">+{peers.length - 1}</span>
            </div>
          )}
        </div>
        {/* Compact controls */}
        <div className="flex items-center justify-between px-3 py-2" style={{ background: "var(--ink)" }}>
          <div className="flex items-center gap-1.5">
            <button onClick={toggleMic} title={micEnabled ? "Mute" : "Unmute"}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${micEnabled ? "" : "bg-rose-600 hover:bg-rose-700"} text-white`}
              style={micEnabled ? { background: "var(--forest-softer)" } : undefined}>
              <MicIcon />
            </button>
            <button onClick={toggleCamera} title={cameraEnabled ? "Camera off" : "Camera on"}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${cameraEnabled ? "" : "bg-rose-600 hover:bg-rose-700"} text-white`}
              style={cameraEnabled ? { background: "var(--forest-softer)" } : undefined}>
              <CamIcon />
            </button>
            <CountdownBadge compact={true} />
          </div>
          <button onClick={handleLeave}
            className="px-3 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded-full text-xs font-medium transition-colors">
            End
          </button>
        </div>
      </div>
    );
  }

  // ── Full-screen call view ─────────────────────────────────────────────────
  const draftStatus = transcribing
    ? "transcribing…"
    : speaking
      ? "hearing you…"
      : vadPaused
        ? "paused"
        : !micEnabled
          ? "mic muted"
          : "listening";
  const showDraftPanel = listening || draft || transcribing;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm" style={{ color: "var(--parchment-dim)" }}>Connecting to call…</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 gap-3">
      {/* Header: harakat toggle · title + live countdown */}
      <div className="flex items-center justify-between gap-3 shrink-0">
        <button
          onClick={() => setShowHarakat((v) => !v)}
          role="switch"
          aria-checked={showHarakat}
          title="Show or hide harakat in the transcript"
          className="surface-glass flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full"
        >
          <span className="relative inline-flex h-5 w-9 rounded-full transition-colors"
            style={{ background: showHarakat ? "var(--gold)" : "var(--forest)" }}>
            <span className="inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform mt-0.5"
              style={{ transform: showHarakat ? "translateX(18px)" : "translateX(2px)" }} />
          </span>
          <span className="text-sm font-medium" style={{ color: "var(--parchment)" }}>Harakat</span>
        </button>
        <div className="text-right">
          <p className="text-base sm:text-lg font-bold leading-tight" style={{ color: "var(--parchment)" }}>
            Practice conversation
          </p>
          <CountdownBadge />
        </div>
      </div>

      {/* 1-minute warning banner */}
      {timeWarning && secsLeft > 0 && (
        <div className="rounded-xl px-4 py-2 text-sm font-medium text-center animate-pulse shrink-0"
          style={{ background: "var(--gold-bg)", color: "var(--gold-bright)", border: "1px solid var(--panel-line)" }}>
          ⏰ Less than 1 minute remaining — call will end automatically
        </div>
      )}

      {/* Body: video tiles stacked as squares on the left, chat filling the
          right. On mobile the tiles become one compact row up top so the
          chat keeps most of the height. */}
      <div className="flex-1 min-h-0 flex flex-col md:flex-row gap-3">
        {/* Participants — mobile: a compact strip (5+ tiles wrap into two
            short rows) so the chat keeps the height; desktop: side column,
            switching to a two-column grid above 5 tiles so ~10 people fit
            without scrolling. Everyone visible in every mode. */}
        {(() => {
          const totalTiles = peers.length + 1; // remotes + self
          let containerCls, containerStyle, tileCls;
          if (isMdUp) {
            if (totalTiles > 5) {
              // Desktop grid: 2 columns, rows share the column height.
              containerCls = "shrink-0 min-h-0 grid grid-cols-2 auto-rows-fr gap-2.5 w-64 lg:w-80 xl:w-96";
              containerStyle = undefined;
              tileCls = "min-w-0 min-h-0 w-full h-full";
            } else {
              // Desktop column: tiles share the height, capped so small
              // groups don't stretch into tall crops.
              containerCls = "shrink-0 min-h-0 flex flex-col gap-2.5 w-48 lg:w-56 xl:w-64";
              containerStyle = undefined;
              tileCls = "flex-1 min-w-0 min-h-0 max-h-44 lg:max-h-52 xl:max-h-56";
            }
          } else if (totalTiles >= 5) {
            // Mobile, big group: two short rows.
            containerCls = "shrink-0 h-40 sm:h-48 grid grid-rows-2 gap-2";
            containerStyle = { gridTemplateColumns: `repeat(${Math.ceil(totalTiles / 2)}, 1fr)` };
            tileCls = "min-w-0 min-h-0 w-full h-full";
          } else {
            // Mobile: single compact strip.
            containerCls = "shrink-0 h-24 sm:h-28 flex gap-2";
            containerStyle = undefined;
            tileCls = "flex-1 min-w-0 min-h-0 w-full h-full";
          }
          return (
            <div className={containerCls} style={containerStyle}>
              {peers.length === 0 ? (
                <div className="relative flex-1 min-w-0 min-h-0 rounded-2xl flex items-center justify-center text-xs"
                  style={{ background: "var(--forest)", border: "1px solid var(--panel-line)", color: "var(--parchment-dim)" }}>
                  {groupCall ? "Waiting for others…" : "Waiting for partner…"}
                </div>
              ) : (
                peers.map(([identity, peer]) => (
                  <VideoTile
                    key={identity}
                    track={peer.videoTrack}
                    label={shortName(peer.name) || (groupCall ? "Guest" : partnerName || "Partner")}
                    active={activeIds.has(identity)}
                    className={tileCls}
                  />
                ))
              )}
              <VideoTile
                track={cameraEnabled ? localVideoTrack : null}
                label={myName || "You"}
                you
                micOff={!micEnabled}
                active={myIdentity ? activeIds.has(myIdentity) : false}
                className={tileCls}
              />
            </div>
          );
        })()}

        {/* Conversation column — chat, suggestions, draft */}
        <div className="flex-1 min-h-0 flex flex-col gap-3">
          {/* Shared transcript — everyone's verified lines, framed as a
              distinct chat panel so the area reads as one window */}
          <div className="flex-1 min-h-0 flex flex-col surface-glass rounded-2xl px-3 pt-2.5 pb-3">
            <div className="flex items-center justify-between pb-2 shrink-0"
              style={{ borderBottom: "1px solid var(--panel-line)" }}>
              <span className="text-[11px] font-semibold tracking-widest uppercase" style={{ color: "var(--gold-dim)" }}>
                💬 Call chat
              </span>
              <span className="text-[10px]" style={{ color: "var(--parchment-dim)" }}>
                visible to everyone on the call
              </span>
            </div>
            <div className="flex-1 min-h-0 flex flex-col pt-2">
              <CallChat
                fill
                messages={messages}
                myName={myName}
                partnerName={partnerName}
                showHarakat={showHarakat}
                onCorrectMessage={correctMessage}
                onSuggestReply={autoAvailable ? (m) => fireAutoSuggest(m.arabic) : undefined}
                emptyText={
                  autoAvailable
                    ? "Turn on Transcript below, speak, review your words, then send — they'll appear here for everyone on the call."
                    : groupCall
                      ? "Messages others send will appear here."
                      : "Messages your partner sends will appear here."
                }
              />
            </div>
          </div>

          {limitReached && (
            <p className="text-xs rounded-lg px-3 py-2 shrink-0"
              style={{ color: "var(--gold-bright)", background: "var(--gold-bg)" }}>
              You've reached today's AI-help limit. The transcript is saved; you can keep talking and use the Speaking Helper.
            </p>
          )}

          {/* Reply suggestions — generated only from a bubble tap */}
          {(suggestBusy || suggestions.length > 0) && (
            <div className="surface-glass rounded-2xl px-3 py-2.5 shrink-0">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] font-semibold tracking-widest uppercase" style={{ color: "var(--gold-dim)" }}>
                  Suggested replies — tap to load into your draft
                </span>
                {suggestions.length > 0 && (
                  <button onClick={() => setSuggestions([])} aria-label="Dismiss suggestions"
                    className="text-xs" style={{ color: "var(--parchment-dim)" }}>✕</button>
                )}
              </div>
              {suggestBusy ? (
                <div className="flex items-center gap-2 text-sm py-1" style={{ color: "var(--parchment-dim)" }}>
                  <Spinner /> Thinking of a reply…
                </div>
              ) : (
                <div className="space-y-1.5">
                  {suggestions.map((r, i) => (
                    <button
                      key={i}
                      onClick={() => pickSuggestion(r)}
                      className="w-full text-left rounded-xl px-3 py-2 dt-ghost-btn"
                    >
                      <p dir="rtl" lang="ar" className="font-arabic text-lg leading-loose" style={{ color: "var(--parchment)" }}>
                        {showHarakat ? r.arabic : stripHarakat(r.arabic)}
                      </p>
                      {r.translation && <p className="text-xs" style={{ color: "var(--parchment-dim)" }}>{r.translation}</p>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Transcript draft — review, then send */}
          {showDraftPanel && (
            <div className="rounded-2xl px-3.5 py-3 shrink-0"
              style={{ border: "1px dashed var(--gold-dim)", background: "rgba(18, 48, 35, 0.35)" }}>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <button onClick={redoDraft} disabled={!draft && !draftTranslation}
                  className="text-xs flex items-center gap-1 disabled:opacity-40"
                  style={{ color: "var(--parchment-dim)" }}>
                  ↺ Redo
                </button>
                <span className="text-xs font-semibold flex items-center gap-1.5" style={{ color: "var(--tag-teal)" }}>
                  Transcript draft · {draftStatus}
                  {(speaking || transcribing) && <Spinner />}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={playDraft}
                  disabled={!lastAudioRef.current}
                  title="Play back what you said"
                  aria-label="Play back what you said"
                  className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center disabled:opacity-40 transition-[filter] hover:brightness-110"
                  style={{ background: "var(--forest-softer)", color: "var(--gold-bright)", border: "1px solid var(--panel-line)" }}
                >
                  <svg className="w-5 h-5 ml-0.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5.14v13.72c0 .84.93 1.35 1.64.9l10.02-6.86a1.07 1.07 0 000-1.8L9.64 4.24A1.07 1.07 0 008 5.14z" />
                  </svg>
                </button>
                <div className="flex-1 min-w-0">
                  <textarea
                    dir="rtl"
                    lang="ar"
                    rows={2}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="…اِبْدَأ بِالكَلام"
                    className="w-full resize-none bg-transparent font-arabic text-xl leading-loose focus:outline-none"
                    style={{ color: "var(--parchment)", border: "none" }}
                  />
                  {draftTranslation && (
                    <p className="text-xs" style={{ color: "var(--parchment-dim)" }}>{draftTranslation}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 mt-1.5">
                <span className="text-xs" style={{ color: "var(--parchment-dim)" }}>✎ Review, then send</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={translateDraft}
                    disabled={!draft.trim() || draftBusy}
                    title="Fix the transcription, add harakat & translation"
                    className="text-xs px-3 py-1.5 rounded-full dt-ghost-btn disabled:opacity-40"
                  >
                    {draftBusy ? "Translating…" : "Translate"}
                  </button>
                  <button
                    onClick={sendDraft}
                    disabled={!draft.trim()}
                    className="text-xs px-4 py-1.5 rounded-full bg-dt-gold-bright text-dt-ink font-semibold hover:brightness-105 disabled:opacity-40 transition-[filter]"
                  >
                    Send ↑
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-2 shrink-0 pb-1">
        {autoAvailable ? (
          <button
            onClick={toggleListening}
            title={listening ? "Stop transcribing your speech" : "Transcribe your speech — review, then send"}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-[filter] ${
              listening ? "bg-dt-gold-bright text-dt-ink hover:brightness-105" : "surface-glass surface--interactive text-dt-parchment"
            }`}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <rect x="3" y="6" width="18" height="12" rx="2" />
              <path strokeLinecap="round" d="M7 14h4m3 0h3M7 10.5h.01M11 10.5h.01M15 10.5h.01" />
            </svg>
            Transcript
          </button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          {onOpenSpeakingHelper && (
            <button onClick={onOpenSpeakingHelper} title="Speaking Helper — say it in Arabic"
              className="w-11 h-11 rounded-full flex items-center justify-center dt-ghost-btn">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M21 12a8 8 0 01-11.3 7.3L3 21l1.7-6.7A8 8 0 1121 12z" />
              </svg>
            </button>
          )}
          <button onClick={toggleMic} title={micEnabled ? "Mute microphone" : "Unmute microphone"}
            className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${micEnabled ? "dt-ghost-btn" : "bg-rose-600 hover:bg-rose-700 text-white"}`}>
            <MicIcon />
          </button>
          <button onClick={toggleCamera} title={cameraEnabled ? "Turn off camera" : "Turn on camera"}
            className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${cameraEnabled ? "dt-ghost-btn" : "bg-rose-600 hover:bg-rose-700 text-white"}`}>
            <CamIcon />
          </button>
          <button onClick={handleLeave}
            title="Leave the call — you can rejoin until the slot ends"
            className="px-4 sm:px-6 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-full text-sm font-medium transition-colors">
            Leave
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function PracticeCallPage({ onClose, minimized = false, onMinimize, onMaximize, onOpenSpeakingHelper, institute = null }) {
  const [serviceDown, setServiceDown] = useState(false);
  const [durationOptions, setDurationOptions] = useState([15, 30, 45, 60]);
  const [maxCallParticipants, setMaxCallParticipants] = useState(2);
  const [state, setState] = useState(null); // from lobby/status API
  const [currentUser, setCurrentUser] = useState(null); // { gender, ... }
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  // Gender prompt for existing users who have no gender set
  const [genderPrompt, setGenderPrompt] = useState("");
  const [genderSaving, setGenderSaving] = useState(false);
  const [genderError, setGenderError] = useState(null);
  // One-time Practice Calls safety pledge (shown before the first join)
  const [showPledge, setShowPledge] = useState(false);
  const [pledgeChecked, setPledgeChecked] = useState(false);
  const [pledgeSaving, setPledgeSaving] = useState(false);
  const [pledgeError, setPledgeError] = useState(null);
  // Post-call report form shown after a call ends
  const [completedCallInfo, setCompletedCallInfo] = useState(null); // { sessionId, partnerName, others }
  const [reportAbout, setReportAbout] = useState(null); // user_id of the co-participant a group report is about
  const [reportIssue, setReportIssue] = useState("");
  const [reportMisgendering, setReportMisgendering] = useState(false);
  const [reportSaving, setReportSaving] = useState(false);
  const [reportDone, setReportDone] = useState(false);
  const [lastChat, setLastChat] = useState([]);          // live transcript from the active call
  const [pastChatsOpen, setPastChatsOpen] = useState(false);
  // Browse / create open-lobby UI
  const [screen, setScreen] = useState("browse"); // "browse" | "create"
  const [openLobbies, setOpenLobbies] = useState({ lobbies: [], total_waiting: 0 });
  const [lobbiesLoading, setLobbiesLoading] = useState(true);
  const [browseDayIdx, setBrowseDayIdx] = useState(0);
  const [joiningId, setJoiningId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  // Action deferred until the one-time pledge is accepted (join / create)
  const pendingActionRef = useRef(null);
  const [lockedUnlockMsg, setLockedUnlockMsg] = useState(false); // flashes "complete 5 topics" message
  const [inCall, setInCall] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [callScheduledEnd, setCallScheduledEnd] = useState(null);
  // Increment to force a full unmount+remount of CallRoom on rejoin
  const [callKey, setCallKey] = useState(0);
  // Persist pre-call config across minimize/maximize (CallRoom remounts on each)
  const [preCallDone, setPreCallDone] = useState(false);
  const [savedMic, setSavedMic] = useState(true);
  const [savedCamera, setSavedCamera] = useState(true);

  const pollingRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const data = await practiceLobbyStatus();
      setState(data);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  // Initial load — check service status and lobby state in parallel
  useEffect(() => {
    setLoading(true);
    Promise.all([
      loadStatus(),
      getUsageLimits().then((limits) => {
        setServiceDown(limits?.practice_calls_disabled ?? false);
        const durs = limits?.practice_call_durations;
        if (Array.isArray(durs) && durs.length) setDurationOptions(durs);
        setMaxCallParticipants(limits?.max_call_participants ?? 2);
      }).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [loadStatus]);

  // Load current user independently so the gender prompt shows as soon
  // as /me responds, without waiting for the lobby status call.
  useEffect(() => {
    fetchCurrentUser().then(setCurrentUser).catch(() => {});
  }, []);

  // Polling when in lobby — clear any existing interval before setting a new one
  useEffect(() => {
    clearInterval(pollingRef.current);
    pollingRef.current = null;
    if (!state) return;
    if (state.in_lobby) {
      pollingRef.current = setInterval(loadStatus, 5000);
    }
    return () => {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    };
  }, [state, loadStatus]);

  // 1-second ticker so the start-countdown re-renders live on the session card
  const [, forceTickRender] = useState(0);
  useEffect(() => {
    const session = state?.session;
    if (!session) return;
    const statusesWithCountdown = ["proposed", "confirmed", "ready"];
    if (!statusesWithCountdown.includes(session.status)) return;
    const endMs = new Date(session.proposed_slot_end).getTime();
    if (Date.now() >= endMs + 5 * 60 * 1000) return; // already expired — no need to tick
    const id = setInterval(() => forceTickRender((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [state]);

  // ── Open-lobby data + actions ──
  const loadLobbies = useCallback(async () => {
    try {
      const data = await practiceOpenLobbies();
      setOpenLobbies({ lobbies: data.lobbies || [], total_waiting: data.total_waiting || 0 });
    } catch (_) {
      /* browse list is best-effort; the status poll surfaces real errors */
    } finally {
      setLobbiesLoading(false);
    }
  }, []);

  // Load + refresh the browse list every 15s while not in a call
  useEffect(() => {
    if (inCall) return;
    loadLobbies();
    const id = setInterval(loadLobbies, 15000);
    return () => clearInterval(id);
  }, [inCall, loadLobbies]);

  /** Gender + level gates shared by join/create. Returns true when eligible. */
  function guardEligibility() {
    const level = state?.level_topic_count ?? 0;
    if (currentUser && !currentUser.gender) {
      setError("Please set your gender above to use practice calls.");
      return false;
    }
    if (level < 5 && level !== 999) {
      setLockedUnlockMsg(true);
      setTimeout(() => setLockedUnlockMsg(false), 4000);
      return false;
    }
    return true;
  }

  /** Run `action` now, or after the one-time pledge is accepted. */
  function requirePledgeThen(action) {
    if (state && !state.pledge_accepted) {
      pendingActionRef.current = action;
      setPledgeChecked(false);
      setPledgeError(null);
      setShowPledge(true);
      return;
    }
    action();
  }

  async function handleAcceptPledge() {
    setPledgeSaving(true);
    setPledgeError(null);
    try {
      await practicePledgeAccept();
      // Reflect acceptance locally so the gate doesn't re-trigger.
      setState((s) => (s ? { ...s, pledge_accepted: true } : s));
      setShowPledge(false);
      const action = pendingActionRef.current;
      pendingActionRef.current = null;
      if (action) await action();
    } catch (e) {
      setPledgeError(e.message);
    } finally {
      setPledgeSaving(false);
    }
  }

  function handleJoinOpenLobby(lobby) {
    if (!guardEligibility()) return;
    requirePledgeThen(async () => {
      setJoiningId(lobby.id);
      setError(null);
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
        await practiceJoinOpenLobby(lobby.id, { timezone: tz });
        await Promise.all([loadStatus(), loadLobbies()]);
      } catch (e) {
        setError(e.message);
        loadLobbies(); // the lobby may have just filled/expired — refresh
      } finally {
        setJoiningId(null);
      }
    });
  }

  function handleOpenCreate() {
    if (!guardEligibility()) return;
    setCreateError(null);
    setScreen("create");
  }

  function handleCreateLobby(payload) {
    requirePledgeThen(async () => {
      setCreating(true);
      setCreateError(null);
      try {
        await practiceCreateOpenLobby(payload);
        setScreen("browse");
        await Promise.all([loadStatus(), loadLobbies()]);
      } catch (e) {
        setCreateError(e.message);
      } finally {
        setCreating(false);
      }
    });
  }

  async function handleEndSession(sessionId) {
    setActionLoading(true);
    setError(null);
    try {
      await practiceSessionComplete(sessionId, { force: true });
      await Promise.all([loadStatus(), loadLobbies()]);
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleCancelOpenLobby(lobbyId) {
    setActionLoading(true);
    setError(null);
    try {
      await practiceCancelOpenLobby(lobbyId);
      await Promise.all([loadStatus(), loadLobbies()]);
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleAccept(sessionId) {
    setActionLoading(true);
    setError(null);
    try {
      await practiceSessionAccept(sessionId);
      await loadStatus();
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDecline(sessionId) {
    setActionLoading(true);
    setError(null);
    try {
      await practiceSessionDecline(sessionId);
      await loadStatus();
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleCancel(sessionId) {
    setActionLoading(true);
    setError(null);
    try {
      await practiceSessionCancel(sessionId);
      await loadStatus();
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleJoinCall(sessionId) {
    setCallScheduledEnd(state?.session?.proposed_slot_end || null);
    setActiveSessionId(sessionId);
    setLastChat([]); // fresh transcript for the new call
    setCallKey((k) => k + 1); // ensure a fresh CallRoom mount
    setPreCallDone(false);     // show pre-call screen for each new session
    setSavedMic(true);
    setSavedCamera(true);
    setInCall(true);
  }

  function handleRejoinCall() {
    // Force a complete unmount+remount of CallRoom — same session, fresh component state
    setCallKey((k) => k + 1);
    setPreCallDone(false);     // show pre-call screen again on explicit rejoin
    setSavedMic(true);
    setSavedCamera(true);
  }

  // ── In-call view ──
  if (inCall && activeSessionId) {
    const handleCallLeave = (partnerName) => {
      // Capture session info for the post-call report form. `others` powers
      // the group-call "who is this about?" chooser.
      const others = (state?.session?.participants || []).filter((p) => !p.is_me);
      setCompletedCallInfo({
        sessionId: activeSessionId,
        partnerName: partnerName || "your partner",
        others,
      });
      setReportAbout(others[0]?.user_id ?? null);
      setReportIssue("");
      setReportMisgendering(false);
      setReportDone(false);
      setInCall(false);
      setActiveSessionId(null);
      onMaximize?.();
      loadStatus();
    };

    // Compact display names — "Aisha Rahman" → "Aisha.R"; never an email.
    const myName = shortName(
      `${currentUser?.first_name || ""} ${currentUser?.last_name || ""}`.trim() ||
        currentUser?.display_name || currentUser?.email || ""
    );
    const activePartnerName = shortName(
      state?.session?.partner_name || state?.session?.partner_username || ""
    );
    const activeGroupCall = (state?.session?.max_participants ?? 2) > 2;

    // When minimized, App.js controls the container — just render the CallRoom directly
    if (minimized) {
      return (
        <CallRoom
          key={callKey}
          sessionId={activeSessionId}
          minimized={true}
          scheduledEnd={callScheduledEnd}
          onLeave={handleCallLeave}
          onRejoin={handleRejoinCall}
          preCallDone={preCallDone}
          initialMic={savedMic}
          initialCamera={savedCamera}
          onPreCallDone={(mic, cam) => { setPreCallDone(true); setSavedMic(mic); setSavedCamera(cam); }}
          onOpenSpeakingHelper={onOpenSpeakingHelper}
          onChatChange={setLastChat}
          myName={myName}
          partnerName={activePartnerName}
          groupCall={activeGroupCall}
          initialMessages={lastChat}
        />
      );
    }

    return (
      <div className="h-full flex flex-col" style={{ background: "var(--ink)" }}>
        <div className="flex items-center justify-between px-4 py-2.5 shrink-0" style={{ borderBottom: "1px solid var(--panel-line)" }}>
          <span className="text-sm font-medium" style={{ color: "var(--parchment-dim)" }}>Practice Call</span>
          <div className="flex items-center gap-3">
            {onMinimize && (
              <button
                onClick={onMinimize}
                title="Minimize — browse the app while on call"
                className="flex items-center gap-1.5 text-dt-parchment-dim hover:text-dt-parchment text-xs px-2.5 py-1.5 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15v4.5M15 15h4.5M15 15l5.25 5.25M9 15H4.5M9 15v4.5M9 15l-5.25 5.25" />
                </svg>
                Minimize
              </button>
            )}
            <button onClick={handleCallLeave} className="text-dt-parchment-dim hover:text-dt-parchment text-sm">✕</button>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-hidden p-3 sm:p-4 flex flex-col max-w-5xl mx-auto w-full">
          <CallRoom
            key={callKey}
            sessionId={activeSessionId}
            minimized={false}
            scheduledEnd={callScheduledEnd}
            onLeave={handleCallLeave}
            onRejoin={handleRejoinCall}
            preCallDone={preCallDone}
            initialMic={savedMic}
            initialCamera={savedCamera}
            onPreCallDone={(mic, cam) => { setPreCallDone(true); setSavedMic(mic); setSavedCamera(cam); }}
            onOpenSpeakingHelper={onOpenSpeakingHelper}
            onChatChange={setLastChat}
            myName={myName}
            partnerName={activePartnerName}
            groupCall={activeGroupCall}
            initialMessages={lastChat}
          />
        </div>
      </div>
    );
  }

  // Not in a call but container is minimized — auto-expand so lobby isn't squished
  if (minimized) { onMaximize?.(); return null; }

  // Practice call service is disabled by admin
  if (serviceDown) {
    return (
      <div className="flex flex-col min-h-screen" style={{ background: "var(--ink)" }}>
        <div className="sticky top-0 z-10 bg-parchment-50/90 backdrop-blur px-4 py-3 flex items-center gap-3">
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h1 className="font-semibold text-slate-800">Practice Calls</h1>
        </div>
        <DiamondDivider className="shrink-0 px-6 py-1" />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="surface-glass rounded-2xl shadow-sm p-8 max-w-sm text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800 mb-1">Practice Calls Unavailable</h2>
              <p className="text-sm text-slate-500 leading-relaxed">
                The practice call service is temporarily unavailable. We're working to restore it as soon as possible.
                Please check back later — we apologise for the inconvenience.
              </p>
            </div>
            <button
              onClick={onClose}
              className="px-5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium transition-colors"
            >
              Go back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Page header ──
  const session = state?.session;
  const myLobby = state?.my_open_lobby;
  const level = state?.level_topic_count ?? 0;

  const sessionStatus = session?.status;
  const isProposed = sessionStatus === "proposed";
  const isConfirmed = sessionStatus === "confirmed";
  const isReady = sessionStatus === "ready";
  const isActive = sessionStatus === "active";

  const isConfirmedByMe = session?.is_confirmed_by_me;

  // Live countdown to start (re-computed each render from the start timestamp)
  const startTime = session ? new Date(session.proposed_slot_start) : null;
  const endTime = session ? new Date(session.proposed_slot_end) : null;
  const nowMs = Date.now();
  const secsUntilStart = startTime ? Math.max(0, Math.round((startTime - nowMs) / 1000)) : 0;
  const isExpired = endTime ? nowMs > endTime.getTime() + 5 * 60 * 1000 : false; // past slot end + 5 min grace
  const isSlotOver = endTime ? nowMs > endTime.getTime() : false; // slot end reached

  const canJoin = (isReady || isActive || isConfirmed) && secsUntilStart === 0 && !isSlotOver;

  return (
    <div className="flex flex-col min-h-screen" style={{ background: "var(--ink)" }}>
      {/* Sticky header — app wordmark centred, like the main app bar */}
      <div className="sticky top-0 z-10 backdrop-blur px-4 py-3" style={{ background: "rgba(14, 33, 26, 0.92)" }}>
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="text-dt-parchment-dim hover:text-dt-parchment text-lg leading-none"
            aria-label="Back"
          >
            ←
          </button>
          <h1 className="font-semibold text-dt-parchment text-sm">Practice Calls</h1>
          <div className="flex-1 flex justify-center">
            <div className="flex flex-col items-center leading-none select-none">
              <span className="font-arabic-display font-bold text-3xl leading-none" style={{ color: "var(--gold-bright)" }} dir="rtl">
                مراجعة
              </span>
              <span className="text-[0.55rem] font-semibold uppercase tracking-[0.28em] mt-1" style={{ color: "var(--gold-dim)" }}>
                murājaʿa
              </span>
            </div>
          </div>
          <button
            onClick={() => setPastChatsOpen(true)}
            title="Past call chats"
            className="flex items-center gap-1.5 text-xs text-dt-parchment-dim hover:text-dt-gold-bright px-2.5 py-1.5 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M21 12a8 8 0 01-11.3 7.3L3 21l1.7-6.7A8 8 0 1121 12z" />
            </svg>
            <span className="hidden sm:inline">Past chats</span>
          </button>
        </div>
      </div>
      {/* Gold diamond divider under the logo (same motif as the main app) */}
      <DiamondDivider className="shrink-0 px-6 py-1" />

      {pastChatsOpen && <PastCallChats onClose={() => setPastChatsOpen(false)} />}

      {/* One-time safety pledge — shown before the first Practice Call */}
      {showPledge && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4 py-6 overflow-auto">
          <div className="surface-glass rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4 my-auto">
            <h2 className="text-lg font-semibold text-dt-parchment">Before you join Practice Calls</h2>
            <DiamondDivider className="py-0.5" />
            <p className="text-sm text-dt-parchment-dim">
              Practice Calls are designed to help learners practise spoken Arabic in a respectful
              environment.
            </p>
            <p className="text-sm text-dt-parchment-dim">By continuing, you confirm that:</p>
            <ul className="space-y-1.5 text-sm text-dt-parchment">
              {[
                "I am at least 18 years old.",
                "My account information is accurate.",
                "I will treat other learners with respect.",
                "I will not record calls.",
                "I will not ask for or share personal contact details.",
                "I understand that misuse may result in my account being suspended.",
              ].map((line) => (
                <li key={line} className="flex gap-2">
                  <span className="shrink-0" style={{ color: "var(--gold)" }}>◆</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>

            <label className="flex items-start gap-3 cursor-pointer pt-1">
              <input
                type="checkbox"
                checked={pledgeChecked}
                onChange={(e) => setPledgeChecked(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded accent-[var(--gold)]"
              />
              <span className="text-sm font-medium text-dt-parchment">I understand and agree.</span>
            </label>

            {pledgeError && (
              <p className="text-xs rounded-lg px-3 py-2" style={{ color: "var(--tag-rose)", background: "var(--tag-rose-bg)" }}>
                {pledgeError}
              </p>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                onClick={() => setShowPledge(false)}
                disabled={pledgeSaving}
                className="px-4 py-2 rounded-lg text-sm font-medium text-dt-parchment-dim hover:text-dt-parchment disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAcceptPledge}
                disabled={!pledgeChecked || pledgeSaving}
                className="px-4 py-2 rounded-xl bg-dt-gold-bright text-dt-ink text-sm font-semibold hover:brightness-105 disabled:opacity-50 transition-[filter]"
              >
                {pledgeSaving ? "Joining…" : "Agree & continue"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-auto px-4 py-6 max-w-xl mx-auto w-full space-y-5">
        {loading && (
          <div className="text-center text-slate-500 text-sm py-12">Loading…</div>
        )}

        {!loading && error && (
          <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-rose-700 text-sm">
            {error}
          </div>
        )}

        {!loading && (
          <>
            {/* ── Post-call report form (shown after a call ends) ── */}
            {completedCallInfo && !reportDone && (
              <div className="surface-glass rounded-2xl shadow-sm p-5 space-y-4">
                <div>
                  <h3 className="text-base font-semibold text-dt-parchment">How was your call?</h3>
                  <p className="text-xs text-dt-parchment-dim mt-0.5">
                    Your call with{" "}
                    <strong className="text-dt-parchment">
                      {(completedCallInfo.others?.length ?? 0) > 1
                        ? completedCallInfo.others.map((p) => shortName(p.name || p.username)).join(", ")
                        : shortName(completedCallInfo.partnerName)}
                    </strong>{" "}
                    has ended. Share optional feedback below.
                  </p>
                </div>

                {(completedCallInfo.others?.length ?? 0) > 1 && (
                  <div>
                    <label className="block text-sm font-medium text-dt-parchment mb-1.5">
                      Who is this feedback about?
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {completedCallInfo.others.map((p) => (
                        <button
                          key={p.user_id}
                          type="button"
                          onClick={() => setReportAbout(p.user_id)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                            reportAbout === p.user_id
                              ? "bg-dt-gold-bright text-dt-ink"
                              : "surface-glass surface--interactive text-dt-parchment"
                          }`}
                        >
                          {shortName(p.name || p.username)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {lastChat.length > 0 && (
                  <div className="rounded-xl p-3" style={{ border: "1px solid var(--panel-line)", background: "rgba(18, 48, 35, 0.35)" }}>
                    <h4 className="text-sm font-semibold mb-2" style={{ color: "var(--gold-bright)" }}>Your conversation</h4>
                    <CallChat
                      messages={lastChat}
                      partnerName={completedCallInfo.partnerName}
                      myName={shortName(
                        `${currentUser?.first_name || ""} ${currentUser?.last_name || ""}`.trim() ||
                          currentUser?.display_name || currentUser?.email || ""
                      )}
                      onSaveMessage={(m) => saveWordBankSentence({ arabic: m.arabic, translation: m.translation || "" })}
                      onCorrectMessage={async (index, m) => {
                        const { arabic, translation } = await correctTranscript(m.arabic);
                        setLastChat((prev) => {
                          const next = prev.map((x, i) => (i === index ? { ...x, arabic, translation: translation || x.translation } : x));
                          if (completedCallInfo?.sessionId) savePracticeCallChat(completedCallInfo.sessionId, next).catch(() => {});
                          return next;
                        });
                      }}
                    />
                    <p className="text-[11px] text-dt-parchment-dim mt-2">Saved sentences appear in Word Bank → Sentences.</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-dt-parchment mb-1">
                    Any issues? (optional)
                  </label>
                  <textarea
                    rows={3}
                    value={reportIssue}
                    onChange={(e) => setReportIssue(e.target.value)}
                    placeholder="Describe any technical or conduct issues…"
                    className="dt-field w-full px-3 py-2 rounded-lg text-sm resize-none"
                  />
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reportMisgendering}
                    onChange={(e) => setReportMisgendering(e.target.checked)}
                    className="mt-0.5 accent-[var(--tag-rose)]"
                  />
                  <span className="text-sm text-dt-parchment">
                    <span className="font-medium" style={{ color: "var(--tag-rose)" }}>Report possible misgendering</span>
                    <span className="block text-xs text-dt-parchment-dim mt-0.5">
                      Flag this if you believe your partner's gender in the system does not match.
                      Multiple independent reports are required before any action is taken.
                    </span>
                  </span>
                </label>
                <div className="flex gap-2 pt-1">
                  <button
                    disabled={reportSaving}
                    onClick={async () => {
                      setReportSaving(true);
                      try {
                        await practiceSessionReport(completedCallInfo.sessionId, {
                          general_issue: reportIssue,
                          flagged_misgendering: reportMisgendering,
                          ...((completedCallInfo.others?.length ?? 0) > 1 && reportAbout != null
                            ? { reported_user_id: reportAbout }
                            : {}),
                        });
                      } catch (_) {}
                      setReportDone(true);
                      setReportSaving(false);
                    }}
                    className="flex-1 py-2 bg-dt-gold-bright text-dt-ink rounded-xl text-sm font-semibold hover:brightness-105 disabled:opacity-50 transition-[filter]"
                  >
                    {reportSaving ? "Submitting…" : "Submit feedback"}
                  </button>
                  <button
                    onClick={() => setCompletedCallInfo(null)}
                    className="px-4 py-2 rounded-xl text-sm dt-ghost-btn"
                  >
                    Skip
                  </button>
                </div>
              </div>
            )}

            {completedCallInfo && reportDone && (
              <div className="rounded-2xl p-4 text-sm flex items-center justify-between gap-3"
                style={{ background: "var(--tag-teal-bg)", border: "1px solid var(--panel-line)", color: "var(--tag-teal)" }}>
                <span>✓ Feedback submitted. Thank you!</span>
                <button
                  onClick={() => setCompletedCallInfo(null)}
                  className="font-medium text-xs hover:brightness-125 transition-[filter]"
                  style={{ color: "var(--tag-teal)" }}
                >
                  Dismiss
                </button>
              </div>
            )}

            {/* ── Gender not set — one-time prompt for existing users ── */}
            {currentUser && !currentUser.gender && !completedCallInfo && (
              <div className="bg-amber-50 rounded-2xl border border-amber-200 p-5 space-y-3">
                <div>
                  <h3 className="text-sm font-semibold text-amber-800">Set your gender to join practice calls</h3>
                  <p className="text-xs text-amber-700 mt-1">
                    Practice calls are gender-segregated. You need to select your gender once — this cannot be changed after saving.
                  </p>
                </div>
                <div className="flex gap-3">
                  {[{ value: "male", label: "Male" }, { value: "female", label: "Female" }].map(({ value, label }) => (
                    <label
                      key={value}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm font-medium transition-colors ${
                        genderPrompt === value
                          ? "border-amber-500 bg-amber-100 text-amber-800"
                          : "border-amber-300 text-amber-700 hover:bg-amber-100"
                      }`}
                    >
                      <input
                        type="radio"
                        name="genderPrompt"
                        value={value}
                        checked={genderPrompt === value}
                        onChange={() => setGenderPrompt(value)}
                        className="sr-only"
                      />
                      {label}
                    </label>
                  ))}
                </div>
                {genderError && <p className="text-xs text-red-600">{genderError}</p>}
                <button
                  disabled={!genderPrompt || genderSaving}
                  onClick={async () => {
                    setGenderSaving(true);
                    setGenderError(null);
                    try {
                      const updated = await updateProfile({ gender: genderPrompt });
                      setCurrentUser((u) => ({ ...u, gender: updated.gender }));
                    } catch (err) {
                      setGenderError(err.message || "Failed to save gender.");
                    } finally {
                      setGenderSaving(false);
                    }
                  }}
                  className="w-full py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-sm font-medium disabled:opacity-50"
                >
                  {genderSaving ? "Saving…" : "Save gender"}
                </button>
              </div>
            )}

            {/* ── Level card — only while speaking practice is still locked ── */}
            {level < 5 && level !== 999 && (
              <div className="surface-glass rounded-2xl shadow-sm p-5">
                <p className="text-xs text-dt-parchment-dim mb-1">Your practice level</p>
                <p className="text-2xl font-bold text-dt-gold-bright">
                  {level === 0 ? "Beginner" : `Topic ${level}`}
                </p>
                <p className="text-xs text-dt-parchment-dim mt-1">
                  🔒 Complete the first 5 topics to unlock speaking practice ({level}/5 done)
                </p>
              </div>
            )}

            {/* ── Active session card ── */}
            {session && (
              <div className="surface-glass rounded-2xl shadow-sm p-5 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    {(() => {
                      const others = (session.participants || []).filter((p) => !p.is_me);
                      const names = others.length
                        ? others.map((p) => shortName(p.name || p.username)).join(", ")
                        : shortName(session.partner_name || session.partner_username);
                      return (
                        <>
                          <p className="text-xs text-slate-500 mb-1">
                            {others.length > 1 ? "Practice partners" : "Practice partner"}
                          </p>
                          <p className="font-semibold text-slate-800">{names}</p>
                          <div className="mt-1.5">
                            <BandBadge band={session.level_band} />
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <StatusBadge status={session.status} map={SESSION_STATUS_LABELS} />
                </div>

                <div className="bg-slate-50 rounded-xl p-3 text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Scheduled</span>
                    <span className="text-slate-700 font-medium">{fmtDate(session.proposed_slot_start)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Duration</span>
                    <span className="text-slate-700">{fmtDuration(session.proposed_slot_start, session.proposed_slot_end)}</span>
                  </div>
                  {isExpired ? (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Status</span>
                      <span className="text-rose-600 font-medium">Expired — slot has passed</span>
                    </div>
                  ) : isSlotOver ? (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Status</span>
                      <span className="text-amber-600 font-medium">Slot ended</span>
                    </div>
                  ) : secsUntilStart > 0 ? (
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Starts in</span>
                      <span className="font-mono font-semibold text-dt-gold-bright">
                        {secsUntilStart >= 3600
                          ? `${Math.floor(secsUntilStart / 3600)}h ${Math.floor((secsUntilStart % 3600) / 60)}m`
                          : secsUntilStart >= 60
                            ? `${Math.floor(secsUntilStart / 60)}m ${secsUntilStart % 60}s`
                            : `${secsUntilStart}s`}
                      </span>
                    </div>
                  ) : (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Status</span>
                      <span className="text-emerald-600 font-semibold animate-pulse">● Live now</span>
                    </div>
                  )}
                </div>

                {/* Confirmation status */}
                {isProposed && (
                  <div className="text-xs text-slate-500 flex gap-3">
                    {(() => {
                      // partner_id is the other user's ID; user_a is one side
                      const partnerConfirmed =
                        session.partner_id === session.user_a
                          ? session.confirmed_by_a
                          : session.confirmed_by_b;
                      return (
                        <>
                          <span className={isConfirmedByMe ? "text-emerald-600" : "text-slate-400"}>
                            You: {isConfirmedByMe ? "✓ Confirmed" : "Waiting…"}
                          </span>
                          <span className={partnerConfirmed ? "text-emerald-600" : "text-slate-400"}>
                            Partner: {partnerConfirmed ? "✓ Confirmed" : "Waiting…"}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex flex-wrap gap-2 pt-1">
                  {isExpired ? (
                    <>
                      <p className="text-xs text-rose-500 w-full text-center py-1">
                        This session has expired. The scheduled time slot has passed.
                      </p>
                      <button
                        disabled={actionLoading}
                        onClick={() => handleCancel(session.id)}
                        className="w-full py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-medium disabled:opacity-50 transition-colors"
                      >
                        {actionLoading ? "Dismissing…" : "Dismiss & find new match"}
                      </button>
                    </>
                  ) : (
                    <>
                      {isProposed && !isConfirmedByMe && (
                        <button
                          disabled={actionLoading}
                          onClick={() => handleAccept(session.id)}
                          className="flex-1 min-w-[120px] py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-medium disabled:opacity-50 transition-colors"
                        >
                          Accept
                        </button>
                      )}
                      {isProposed && (
                        <button
                          disabled={actionLoading}
                          onClick={() => handleDecline(session.id)}
                          className="flex-1 min-w-[100px] py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-xl text-sm disabled:opacity-50 transition-colors"
                        >
                          Decline
                        </button>
                      )}
                      {(isConfirmed || isReady) && canJoin && (
                        <button
                          disabled={actionLoading}
                          onClick={() => handleJoinCall(session.id)}
                          className="flex-1 min-w-[120px] py-2 bg-dt-gold-bright text-dt-ink rounded-xl text-sm font-semibold hover:brightness-105 disabled:opacity-50 transition-[filter]"
                        >
                          Join Call Now
                        </button>
                      )}
                      {(isConfirmed || isReady) && !canJoin && !isSlotOver && secsUntilStart > 0 && (
                        <p className="text-xs text-slate-500 w-full text-center py-1">
                          Join button will appear when the call starts.
                        </p>
                      )}
                      {isActive && !isSlotOver && (
                        <>
                          <button
                            onClick={() => handleJoinCall(session.id)}
                            className="flex-1 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-sm font-medium"
                          >
                            Rejoin Active Call
                          </button>
                          {(session.max_participants ?? 2) > 2 ? (
                            // Group call: anyone (host included) can leave for
                            // good — the call continues while ≥2 remain.
                            <button
                              disabled={actionLoading}
                              onClick={() => handleEndSession(session.id)}
                              className="w-full py-1.5 text-xs text-slate-400 hover:text-rose-600 disabled:opacity-50"
                            >
                              Leave this call — the group continues without you
                            </button>
                          ) : myLobby?.is_host ? (
                            // The host can't end early — their guest may still rejoin.
                            // Once the guest leaves for good, the lobby reopens and the
                            // "Your lobby" card offers Cancel lobby.
                            <p className="text-[11px] text-slate-400 w-full text-center py-1">
                              Your partner can rejoin until the slot ends. If they leave for
                              good, your lobby reopens and you can cancel it.
                            </p>
                          ) : (
                            <button
                              disabled={actionLoading}
                              onClick={() => handleEndSession(session.id)}
                              className="w-full py-1.5 text-xs text-slate-400 hover:text-rose-600 disabled:opacity-50"
                            >
                              Leave lobby — free your spot for someone else
                            </button>
                          )}
                        </>
                      )}
                      {(isProposed || isConfirmed || isReady) && (
                        <button
                          disabled={actionLoading}
                          onClick={() => handleCancel(session.id)}
                          className="w-full py-1.5 text-xs text-slate-400 hover:text-rose-600 disabled:opacity-50"
                        >
                          Cancel session
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* ── Create-lobby screen ── */}
            {!session && screen === "create" && (
              <CreateLobbyScreen
                onBack={() => setScreen("browse")}
                onCreate={handleCreateLobby}
                creating={creating}
                error={createError}
                durationOptions={durationOptions}
                maxParticipants={maxCallParticipants}
                institute={institute}
              />
            )}

            {/* ── Browse open lobbies ── */}
            {!session && screen === "browse" && (
              <>
                {/* Your open lobby */}
                {myLobby && (
                  <div className="surface-glass rounded-2xl shadow-sm p-5 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2.5">
                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-dt-gold-bright animate-pulse" />
                        <h3 className="font-semibold text-dt-parchment">
                          {myLobby.is_host ? "Your lobby" : "Lobby you joined"}
                        </h3>
                      </div>
                      <BandBadge band={myLobby.level_band} />
                    </div>
                    <p className="text-sm text-dt-parchment">
                      {fmtDate(myLobby.start_utc)} · {myLobby.duration_minutes} min
                      {myLobby.strict_level_matching && (
                        <span className="inline-flex items-center gap-1 ml-2 text-xs text-dt-gold-dim">
                          <LockIcon /> strict
                        </span>
                      )}
                      {myLobby.is_institute_only && (
                        <span
                          className="inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold"
                          style={{ color: "var(--tag-teal)", background: "var(--tag-teal-bg)" }}
                        >
                          Institute only
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-dt-parchment-dim">
                      {myLobby.waiting_count >= myLobby.capacity
                        ? `${myLobby.waiting_count} of ${myLobby.capacity} joined — check your email to confirm the call.`
                        : (myLobby.capacity ?? 2) > 2
                          ? `${myLobby.waiting_count} of ${myLobby.capacity} joined — the call starts on time with everyone who has joined (at least 2).`
                          : `${myLobby.waiting_count} of ${myLobby.capacity} joined — we'll email you as soon as a partner joins your lobby.`}
                    </p>
                    {myLobby.status !== "full" && (
                      <button
                        disabled={actionLoading}
                        onClick={() => handleCancelOpenLobby(myLobby.id)}
                        className="w-full py-2 rounded-xl text-sm surface-glass surface--interactive text-dt-parchment-dim hover:text-dt-parchment disabled:opacity-50"
                      >
                        {actionLoading
                          ? myLobby.is_host ? "Cancelling…" : "Leaving…"
                          : myLobby.is_host ? "Cancel lobby" : "Leave lobby"}
                      </button>
                    )}
                  </div>
                )}

                {/* Open lobbies header */}
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-2xl font-bold text-dt-parchment">Open lobbies</h2>
                    <p className="text-sm text-dt-parchment-dim mt-0.5">
                      {openLobbies.total_waiting === 1
                        ? "1 student waiting to practice"
                        : `${openLobbies.total_waiting} students waiting to practice`}
                    </p>
                  </div>
                  <span className="flex items-center gap-1.5 text-xs text-dt-parchment-dim shrink-0 mt-1">
                    🕐 {Intl.DateTimeFormat().resolvedOptions().timeZone}
                  </span>
                </div>

                <DateChips count={5} selectedIdx={browseDayIdx} onSelect={setBrowseDayIdx} />

                {/* Locked message (topic gate) */}
                {lockedUnlockMsg && (
                  <p className="text-sm rounded-lg px-3 py-2" style={{ color: "var(--gold-bright)", background: "var(--gold-bg)" }}>
                    🔒 Complete your first 5 topics to unlock speaking practice. ({level}/5 done)
                  </p>
                )}

                {/* Timeline of open lobbies for the selected day */}
                {(() => {
                  const day = nextDays(browseDayIdx + 1)[browseDayIdx];
                  const dayLobbies = openLobbies.lobbies.filter((l) => sameLocalDay(l.start_utc, day));
                  const morning = dayLobbies.filter((l) => new Date(l.start_utc).getHours() < 12);
                  const later = dayLobbies.filter((l) => new Date(l.start_utc).getHours() >= 12);

                  const renderRow = (l) => (
                    <div key={l.id} className="flex gap-3">
                      <div className="w-14 shrink-0 flex flex-col items-end">
                        <span className="text-sm font-semibold text-dt-parchment tabular-nums">{fmtClock(l.start_utc)}</span>
                        <span
                          className="flex-1 w-px mt-1 mr-5"
                          style={{ background: "linear-gradient(180deg, var(--gold-dim), transparent)", opacity: 0.4 }}
                        />
                      </div>
                      <div className="flex-1 surface-glass rounded-2xl p-4 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <BandBadge band={l.level_band} />
                            {l.strict_level_matching && <LockIcon className="w-3.5 h-3.5 text-dt-gold-dim" />}
                            {l.is_institute_only && (
                              <span
                                className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                                style={{ color: "var(--tag-teal)", background: "var(--tag-teal-bg)" }}
                                title="Only members of your institute can see this lobby"
                              >
                                Institute
                              </span>
                            )}
                            <span className="text-sm text-dt-parchment-dim">{l.duration_minutes} min</span>
                          </div>
                          <p className="text-xs text-dt-parchment-dim mt-2">
                            <span className="text-dt-gold-bright">●</span> {fmtUntil(l.start_utc)}
                            <span className="mx-1.5">·</span>
                            {(l.capacity ?? 2) > 2
                              ? `group call · ${l.waiting_count} of ${l.capacity} joined`
                              : `${l.waiting_count} waiting`}
                          </p>
                        </div>
                        <button
                          disabled={joiningId === l.id}
                          onClick={() => handleJoinOpenLobby(l)}
                          className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-dt-gold-bright text-dt-ink hover:brightness-105 disabled:opacity-50 shrink-0 transition-[filter]"
                        >
                          {joiningId === l.id ? "Joining…" : "Join"}
                        </button>
                      </div>
                    </div>
                  );

                  return (
                    <div className="space-y-4">
                      {lobbiesLoading && dayLobbies.length === 0 && (
                        <p className="text-sm text-dt-parchment-dim text-center py-6">Loading lobbies…</p>
                      )}
                      {!lobbiesLoading && dayLobbies.length === 0 && (
                        <p className="text-sm text-dt-parchment-dim text-center py-6">
                          No open lobbies on this day yet — start your own below.
                        </p>
                      )}
                      {morning.length > 0 && (
                        <>
                          <p className="text-xs font-semibold tracking-widest uppercase text-dt-parchment-dim">Morning</p>
                          <div className="space-y-3">{morning.map(renderRow)}</div>
                        </>
                      )}
                      {later.length > 0 && (
                        <>
                          <p className="text-xs font-semibold tracking-widest uppercase text-dt-parchment-dim pt-1">
                            Afternoon · Evening
                          </p>
                          <div className="space-y-3">{later.map(renderRow)}</div>
                        </>
                      )}
                    </div>
                  );
                })()}

                {/* Start-your-own CTA */}
                {!myLobby && (
                  <button
                    onClick={handleOpenCreate}
                    className="w-full rounded-2xl border border-dashed p-4 flex items-center justify-between gap-3 text-left transition-colors"
                    style={{ borderColor: "var(--gold-dim)" }}
                  >
                    <span>
                      <span className="block text-sm text-dt-parchment">Nothing fits your schedule?</span>
                      <span className="block text-base font-semibold text-dt-gold-bright mt-0.5">
                        Start a lobby at your own time
                      </span>
                    </span>
                    <span className="text-2xl text-dt-gold-bright leading-none shrink-0">+</span>
                  </button>
                )}

                {/* ── Info section ── */}
                <div className="rounded-xl border p-4 space-y-2" style={{ borderColor: "var(--panel-line)" }}>
                  <h3 className="text-xs font-semibold text-dt-parchment-dim uppercase tracking-wide">How it works</h3>
                  <ol className="text-sm text-dt-parchment-dim space-y-1 list-decimal list-inside">
                    <li>Join an open lobby — or start your own at a time that suits you</li>
                    <li>When a partner joins, you both get an email to confirm the call</li>
                    <li>Both students accept the proposed time</li>
                    <li>At call time, join the private video room from this page</li>
                  </ol>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
